import {
    Component,
    ElementRef,
    inject,
    input,
    output,
    viewChild,
    computed,
    linkedSignal,
} from '@angular/core';
import { MatButtonToggleChange, MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { PageEvent } from '@angular/material/paginator';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { EventsPaginatedService } from '@app/shared/services/events-paginated.service';
import {
    ActiveColumnFilter,
    SearchStore,
} from '@app/stores/searchStore';
import { AppStore } from '@app/stores/appStore';
import { EventsStore } from '@app/stores/eventsStore';
import {
    ViewHeaderAction,
    ViewType,
} from '@app/shared/models/view-config.interface';
import { ViewConfigurationService } from '@app/shared/services/view-config.service';
import { SelectableItem } from '@app/stores/selectionStore';
import { MatFormField, MatLabel, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatCheckbox } from '@angular/material/checkbox';
import { NgClass } from '@angular/common';
import { MatChip, MatChipRemove, MatChipSet } from '@angular/material/chips';
import { ReactiveFormsModule } from '@angular/forms';

@Component({
    selector: 'app-paginated-table-filter',
    templateUrl: './paginated-table-filter.component.html',
    styleUrls: ['./paginated-table-filter.component.scss'],
    imports: [
        MatFormField,
        MatLabel,
        MatIcon,
        MatPrefix,
        MatInput,
        MatButtonToggleGroup,
        MatButtonToggle,
        MatButton,
        MatTooltip,
        MatCheckbox,
        NgClass,
        MatChipSet,
        MatChip,
        MatChipRemove,
        ReactiveFormsModule,
        TranslateModule,
    ],
})
export class PaginatedTableFilterComponent {
    private appStore = inject(AppStore);
    private searchStore = inject(SearchStore);
    private eventsStore = inject(EventsStore);
    filterSearch = viewChild<ElementRef<HTMLInputElement>>('filterSearch');

    showClosedLabelInput = input<string>('OUTGOING-GOODS.SHOW-CLOSED-NOTES', {
        alias: 'showClosedLabel',
    });
    // Writable, seeded by the input; internal view-type changes update it
    // while parent binding changes re-seed it.
    showClosedLabel = linkedSignal(() => this.showClosedLabelInput());
    enableAdvancedFilters = input<boolean>(true);
    totalItems = input<number>(0);
    userRole = input<'consignor' | 'consignee' | 'carrier' | 'admin'>(
        'consignor'
    );
    shouldShowLocationFilter = input<boolean>(false);
    shouldShowColumnFilter = input<boolean>(false);
    filteredLocationsCount = input<number>(0);
    filteredColumnsCount = input<number>(0);
    isLocationFilterActive = input<boolean>(false);
    headerActions = input<ViewHeaderAction[]>([]);
    currentSelection = input<SelectableItem[]>([]);
    displayClosedToggle = input<boolean>(true);

    locationFilterClicked = output<void>();
    columnFilterClicked = output<void>();
    headerActionClicked = output<string>();

    // Local component state
    viewType = 'folder';

    // Signals for reactive state
    currentViewType = this.searchStore.currentViewType;

    // Computed values from search state
    searchText = this.searchStore.searchText;
    /** Filters applied through the column headers, one chip each. */
    columnFilters = this.searchStore.columnFilters;
    showClosed = computed(() => this.appStore.showClosed());
    /**
     * The toggle only means something where `loadData` forwards it as
     * `filterNotClosed` - which every bundle, delivery-note and tour view does.
     * Notification rows are events, not records that can be closed, so the
     * checkbox was rendered but ignored there.
     */
    showClosedToggleVisible = computed(
        () => this.currentViewType() !== 'notifications'
    );
    /** Hidden where the endpoint has no full-text `search` (bundle groups). */
    searchVisible = computed(() => !this.searchStore.singleColumnFilterOnly());
    loading = this.searchStore.loading;

    // Computed values for view switching
    viewToggleConfigComputed = computed(() => {
        return this.viewConfigService.getViewToggleConfig(this.userRole());
    });

    availableViews = computed(() => {
        const config = this.viewToggleConfigComputed();
        return (config.views || []).filter(view => {
            if (view.visible === false) return false;
            if (typeof view.visible === 'function') {
                return view.visible(); // Call the function to determine visibility
            }
            return view.visible == null || view.visible === true; // Default to true if not specified
        });
    });

    shouldShowViewToggle = computed(() => {
        return this.availableViews().length > 1;
    });
    pagination = this.searchStore.pagination;

    // Computed pagination values
    currentPage = computed(
        () => Math.floor(this.pagination().offset / this.pagination().limit) + 1
    );
    totalPages = computed(() =>
        Math.ceil(this.totalItems() / this.pagination().limit)
    );

    constructor(
        private translate: TranslateService,
        private sessionService: SessionService,
        private viewConfigService: ViewConfigurationService,
        private eventsPaginatedService: EventsPaginatedService
    ) {}

    /** Reads the newNotifications signal from eventsStore. */
    get hasNewNotifications(): boolean {
        return this.eventsStore.newNotifications();
    }

    /**
     * Chip text for a filter value. Option-based filters (`boolean`, `status`,
     * `select`) carry the raw wire value (`'true'`, `'open'`,
     * `'self_checkin_driver_waiting'`, ...) plus the label the popover showed,
     * which is what belongs on the chip; text and date filters are shown
     * verbatim.
     */
    chipValue(filter: ActiveColumnFilter): string {
        return filter.valueLabel
            ? this.translate.instant(filter.valueLabel)
            : filter.value;
    }

    removeColumnFilter(keyName: string): void {
        this.searchStore.removeColumnFilter(keyName);
    }

    clearColumnFilters(): void {
        this.searchStore.clearColumnFilters();
    }

    onSearchTextChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const text = input.value;
        this.updateSearchText(text);
    }

    private updateSearchText(text: string): void {
        // Filter out session email address to prevent accidental search
        const userSession = this.sessionService.getSession();
        if (text === userSession?.emailAddress) {
            this.resetFilter();
            return;
        }

        this.searchStore.updateSearchText(text);
    }

    onShowClosedChange(showClosed: boolean): void {
        this.appStore.setShowClosed(showClosed);

        // Trigger data refresh to reload with new filter
        this.searchStore.triggerDataRefresh();
    }

    /** The search bar's delete icon: clears the text *and* every column filter. */
    resetFilter(): void {
        if (this.filterSearch()?.nativeElement) {
            this.filterSearch()!.nativeElement.value = '';
        }
        this.searchStore.updateSearchText('');
        this.searchStore.clearColumnFilters();
    }

    // View type switching
    onViewTypeChange(event: MatButtonToggleChange): void {
        const newViewType = event.value as ViewType;

        // Get the search config for the new view type
        const viewConfig = this.viewConfigService.getViewConfig(
            this.userRole(),
            newViewType
        );
        const searchConfig = viewConfig?.searchConfig;

        // Clear notifications when switching to notifications view
        if (newViewType === 'notifications') {
            this.eventsPaginatedService.setNewNotificationsToFalse();
        }

        // update show-close label
        switch (newViewType) {
            case 'folders':
                this.showClosedLabel.set('OUTGOING-GOODS.SHOW-CLOSED');
                break;
            case 'deliverynotes':
                this.showClosedLabel.set('OUTGOING-GOODS.SHOW-CLOSED-NOTES');
                break;
            case 'tours':
                this.showClosedLabel.set('SPEDITEUR.TABLE.SHOW-CLOSED');
                break;
        }

        // updateCurrentViewType clears searchText and columnFilters - a filter
        // param from the old view would make the new endpoint answer with a 400.
        this.searchStore.updateCurrentViewType(newViewType, searchConfig);
    }

    // Pagination methods
    onPageChange(event: PageEvent): void {
        const offset = event.pageIndex * event.pageSize;
        this.searchStore.updatePagination({ limit: event.pageSize, offset });
    }

    // Location filter methods
    onLocationFilterClick(): void {
        this.locationFilterClicked.emit();
    }

    isFilteringLocations(): boolean {
        return this.isLocationFilterActive();
    }

    getFilteredLocationsCount(): number {
        return this.filteredLocationsCount();
    }

    // Column filter methods
    onColumnFilterClick(): void {
        this.columnFilterClicked.emit();
    }

    // Action button methods
    onHeaderActionClick(actionId: string): void {
        this.headerActionClicked.emit(actionId);
    }

    isHeaderActionVisible(actionId: string): boolean {
        const action = this.headerActions().find(a => a.id === actionId);
        if (!action) return false;
        return action.visible
            ? action.visible(undefined, this.currentSelection())
            : true;
    }

    isHeaderActionDisabled(actionId: string): boolean {
        const action = this.headerActions().find(a => a.id === actionId);
        if (!action) return true;
        return action.disabled
            ? action.disabled(undefined, this.currentSelection())
            : false;
    }
}
